// @ts-strict-ignore
import CardTitle from "@dashboard/components/CardTitle";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import Money from "@dashboard/components/Money";
import {
  CustomerDetailsQuery,
  useVerdaCreditUpdateMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages, commonMessages } from "@dashboard/intl";
import {
  Card,
  CardActions,
  CardContent,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  title: {
    id: "vErDaC01",
    defaultMessage: "Verda credit",
    description: "customer verda credit card title",
  },
  verdaUid: {
    id: "vErDaC07",
    defaultMessage: "Verda member ID",
    description: "customer verda uid field",
  },
  notLinked: {
    id: "vErDaC08",
    defaultMessage: "This customer is not linked to a Verda account.",
    description: "customer verda not linked",
  },
  totalCredit: {
    id: "vErDaC02",
    defaultMessage: "Total credit",
    description: "customer verda credit field",
  },
  usedCredit: {
    id: "vErDaC03",
    defaultMessage: "Used credit",
    description: "customer verda credit field",
  },
  allowanceCredit: {
    id: "vErDaC04",
    defaultMessage: "Available credit",
    description: "customer verda credit field",
  },
  noCredit: {
    id: "vErDaC05",
    defaultMessage: "This customer has no Verda credit record.",
    description: "customer verda credit empty state",
  },
  updateError: {
    id: "vErDaC06",
    defaultMessage: "Failed to update Verda credit.",
    description: "customer verda credit error",
  },
});

const useStyles = makeStyles(
  theme => ({
    readOnlyRow: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: theme.spacing(1.5),
    },
    input: {
      marginBottom: theme.spacing(1),
    },
  }),
  { name: "CustomerVerdaCredit" },
);

export interface CustomerVerdaCreditProps {
  customerId: string;
  verdaUid: CustomerDetailsQuery["user"]["verdaUid"];
  verdaCredit: CustomerDetailsQuery["user"]["verdaCredit"];
  disabled?: boolean;
}

const CustomerVerdaCredit: React.FC<CustomerVerdaCreditProps> = ({
  customerId,
  verdaUid,
  verdaCredit,
  disabled,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const notify = useNotifier();

  // Local copy of the credit so used/available update after a successful edit
  // (the mutation result carries no id/__typename, so Apollo can't normalize
  // it into customer.verdaCredit). Re-synced whenever the query prop changes.
  const [credit, setCredit] = React.useState(verdaCredit);
  React.useEffect(() => setCredit(verdaCredit), [verdaCredit]);

  // Editable total, seeded from the credit and re-synced when it changes
  // (initial load, refetch, or after our own mutation).
  const [totalCredit, setTotalCredit] = React.useState<string>("");
  React.useEffect(() => {
    setTotalCredit(credit?.totalCredit != null ? String(credit.totalCredit) : "");
  }, [credit?.totalCredit]);

  const [updateVerdaCredit, opts] = useVerdaCreditUpdateMutation({
    onCompleted: data => {
      const errors = data.verdaCreditUpdate?.errors ?? [];
      if (errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges),
        });
        const updated = data.verdaCreditUpdate?.verdaCredit;
        if (updated) {
          setCredit(updated);
        }
      } else {
        notify({
          status: "error",
          text: errors[0]?.message ?? intl.formatMessage(messages.updateError),
        });
      }
    },
  });

  const currency = credit?.currency ?? "";
  const trimmedTotal = totalCredit.trim();
  const numericTotal = Number(trimmedTotal);
  const isValidTotal =
    trimmedTotal !== "" && Number.isFinite(numericTotal) && numericTotal >= 0;

  const renderAmount = (value?: number | null) =>
    value != null ? (
      <Money money={{ amount: value, currency }} />
    ) : (
      <Typography variant="caption">—</Typography>
    );

  return (
    <Card>
      <CardTitle title={intl.formatMessage(messages.title)} />
      <CardContent>
        <TextField
          className={classes.input}
          disabled
          fullWidth
          label={intl.formatMessage(messages.verdaUid)}
          value={verdaUid ?? ""}
          helperText={
            !verdaUid ? intl.formatMessage(messages.notLinked) : undefined
          }
        />
        {credit ? (
          <>
            <TextField
              className={classes.input}
              disabled={disabled || opts.loading}
              fullWidth
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              label={intl.formatMessage(messages.totalCredit)}
              name="totalCredit"
              value={totalCredit}
              onChange={event => setTotalCredit(event.target.value)}
            />
            <div className={classes.readOnlyRow}>
              <Typography variant="caption">
                {intl.formatMessage(messages.usedCredit)}
              </Typography>
              {renderAmount(credit.usedCredit)}
            </div>
            <div className={classes.readOnlyRow}>
              <Typography variant="caption">
                {intl.formatMessage(messages.allowanceCredit)}
              </Typography>
              {renderAmount(credit.allowanceCredit)}
            </div>
          </>
        ) : (
          verdaUid && (
            <Typography variant="caption">
              {intl.formatMessage(messages.noCredit)}
            </Typography>
          )
        )}
      </CardContent>
      {credit && (
        <CardActions>
          <ConfirmButton
            transitionState={opts.status}
            disabled={disabled || !isValidTotal}
            onClick={() =>
              updateVerdaCredit({
                // Send the raw string (not Number()) so exact decimals are
                // preserved end-to-end (PositiveDecimal parses the string).
                variables: { id: customerId, totalCredit: trimmedTotal },
              })
            }
          >
            {intl.formatMessage(buttonMessages.save)}
          </ConfirmButton>
        </CardActions>
      )}
    </Card>
  );
};

CustomerVerdaCredit.displayName = "CustomerVerdaCredit";
export default CustomerVerdaCredit;
